import builder, { Builder } from '@builder.io/react';
import { InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { awaitBuilderData, registerComponents } from '../builder';
import { getPageFromPageID } from '../helpers/BuilderHelper';
import { ALL_BUSINESS_PREFIXES, EXCLUDE_PAGES, LOCALE_LINKS, Locale, getLocaleFromRoute, localifyLink, stripLocaleFromURL, useLocale } from '../helpers/LangHelper';
import BusinessHubModel from '../models/BusinessHub';
import { BusinessHubPage } from '../modules/BusinessHubPage';
import { MainPage } from '../modules/MainPage';
import { setCookie } from '../helpers/cookieUtils';
import { NotFound } from '@/components/not-found';

registerComponents();

enum PageModel {
    PAGE = 'page',
    BUSINESS_HUB = 'business-hub',
}

export async function getStaticProps(context: any) {
    const strippedLocaleURL = stripLocaleFromURL(context.params.slug.join('/') ?? '');
    let pageSlug = strippedLocaleURL;
    const lang: Locale = context.params.slug[0];

    if (ALL_BUSINESS_PREFIXES.includes(pageSlug.replace(/\//g, ''))) {
        pageSlug = '/business';
    }

    let page;
    let model: PageModel = PageModel.PAGE;

    try {
        page = await builder
            .get('page', {
                userAttributes: {
                    urlPath: `${localifyLink(pageSlug, Locale.Australia)}`,
                    locale: lang,
                },
            })
            .toPromise();

        if (!page) {
            page = await BusinessHubModel.getPage(`${localifyLink(pageSlug, Locale.Australia)}`, lang);
            if (page) model = PageModel.BUSINESS_HUB;
        }

        if (page) await awaitBuilderData(page, lang);
    } catch (ex) {}

    let canonicalUrl = null;
    const host = LOCALE_LINKS[lang]?.host;
    if (host) {
        canonicalUrl = `${host}/${lang}${strippedLocaleURL}`;
    }

    return {
        props: { page: page ?? null, model, canonicalUrl },
        revalidate: 3600,
    };
}

export async function getStaticPaths() {
    const getPages = async (pageModel = PageModel.PAGE, offset = 0) => {
        const limit = 100;
        let currentPages = await builder.getAll(pageModel, {
            options: {
                noTargeting: true,
            },
            fields: 'query,data.url',
            limit,
            offset,
        });

        if (currentPages.length == limit) {
            currentPages = [...currentPages, ...(await getPages(pageModel, offset + limit))];
        }

        return currentPages;
    };

    let pages = [...(await getPages()), ...(await getPages(PageModel.BUSINESS_HUB))];

    const allPaths: string[] = [];

    pages.forEach((page) => {
        if (!EXCLUDE_PAGES.includes(page.data?.url)) {
            for (const queryParam of (page as any).query) {
                // Query is definitely a parameter. Seems like the types aren't updated.
                if (queryParam.property === 'locale') {
                    for (const pageLocale of queryParam.value) {
                        if (pageLocale !== Locale.USA) {
                            allPaths.push(localifyLink(`/${pageLocale}${page?.data?.url}`, pageLocale));
                        }
                    }
                }
            }
        }
    });

    return {
        paths: allPaths,
        fallback: true,
    };
}

const DefaultPage = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
    const { page, model } = props;

    const { locale } = useLocale();
    const router = useRouter();
    const [previewPage, setPreviewPage] = useState<any>(page);
    const [previewModel, setPreviewModel] = useState<PageModel>(model);

    const _getBuilderPreviewPage = async () => {
        const locale: Locale = getLocaleFromRoute(router.asPath);

        const pageId = router.query['builder.overrides.page'] as string;
        if (pageId) {
            const page = await getPageFromPageID(pageId as string);
            if (page) {
                await awaitBuilderData(page, locale);
                setPreviewPage(page);
                setPreviewModel(PageModel.PAGE);
            }
        }

        const businessHubId = router.query['builder.overrides.business-hub'] as string;
        if (businessHubId) {
            const testVariant = router.query[`builder.tests.${businessHubId}`];
            if (testVariant) {
                setCookie(`builder.tests.${businessHubId}`, `${testVariant}`);
            }

            const businessHubPage = await BusinessHubModel.findById(businessHubId);
            if (businessHubPage) {
                await awaitBuilderData(businessHubPage, locale);
                setPreviewPage(businessHubPage);
                setPreviewModel(PageModel.BUSINESS_HUB);
            }
        }
    };

    useEffect(() => {
        // let cancelled = false;

        if (!(Builder.isEditing || Builder.isPreviewing)) return;
        if (page) return;
        if (router.isFallback) return;

        _getBuilderPreviewPage();

        // return () => {
        //     cancelled = false;
        // };
    }, [page, router]);

    useEffect(() => {
        setPreviewPage(page);
    }, [page]);

    useEffect(() => {
        setPreviewModel(model);
    }, [model]);

    if (router.isFallback) {
        return <h1>Loading...</h1>;
    }

    if (previewPage === null) {
        return <NotFound locale={locale} />;
    }

    if (previewModel === PageModel.BUSINESS_HUB) {
        return <BusinessHubPage page={previewPage} />;
    }

    return <MainPage page={previewPage} />;
};

export default DefaultPage;
